'use client';

import Box from '@enlighten/design-system/dist/Box';
import Fade from '@enlighten/design-system/dist/Fade';
import Grid from '@enlighten/design-system/dist/Grid';
import List from '@enlighten/design-system/dist/List';
import ListItem from '@enlighten/design-system/dist/ListItem';
import ListItemButton from '@enlighten/design-system/dist/ListItemButton';
import type { Theme } from '@enlighten/design-system/dist/styles';
import Typography from '@enlighten/design-system/dist/Typography';
import useMediaQuery from '@enlighten/design-system/dist/useMediaQuery';
import Image from 'next/image';
import { useState } from 'react';

import MonitoringImage1Mobile from '@/assets/images/pages/bizking/cnt_mo_monitor_1.png';
import MonitoringImage2Mobile from '@/assets/images/pages/bizking/cnt_mo_monitor_2.png';
import MonitoringImage3Mobile from '@/assets/images/pages/bizking/cnt_mo_monitor_3.png';
import MonitoringImage1Pc from '@/assets/images/pages/bizking/cnt_pc_monitor_1.png';
import MonitoringImage2Pc from '@/assets/images/pages/bizking/cnt_pc_monitor_2.png';
import MonitoringImage3Pc from '@/assets/images/pages/bizking/cnt_pc_monitor_3.png';

const tabs = [
	{
		image: {
			desktop: MonitoringImage1Pc,
			mobile: MonitoringImage1Mobile,
		},
		label: '이상감지',
		value: 'detect',
		description: `고객 만족 향상을 위해 발전소 문제 발생 시 빠르고 정확하게 대처해야 합니다.
발전왕 비즈에선 모든 발전소의 이상상태를 한눈에 확인할 수 있습니다.`,
	},
	{
		image: {
			desktop: MonitoringImage2Pc,
			mobile: MonitoringImage2Mobile,
		},
		label: '지역별 확인',
		value: 'check',
		description: `지역별로 현장 확인이 필요한 발전소와 가장 가까운 협력사의 위치를 확인할 수 있습니다.
문제 발생 시 빠르고 정확하게 현장출동을 할 수 있습니다.`,
	},
	{
		image: {
			desktop: MonitoringImage3Pc,
			mobile: MonitoringImage3Mobile,
		},
		label: '집중 관리',
		value: 'manage',
		description: `집중 관리가 필요한 발전소들을 한곳에서 모니터링할 수 있습니다.
지난 30일 발전 현황과 실시간 상태를 비교하여 관리가 필요한 발전소를 한눈에 파악할 수 있습니다.`,
	},
];

export default function Section2() {
	const [tab, setTab] = useState<'detect' | 'check' | 'manage'>('detect');

	const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'));

	return (
		<Grid container justifyContent="center" width="100%">
			<Grid container direction={{ xs: 'column', md: 'row' }} width="100%" maxWidth={1440} padding={{ xs: '40px 0', md: 0 }}>
				<Grid container direction="column" justifyContent="center" flex={{ xs: 'none', md: 1 }} paddingX="20px">
					<Typography fontSize={{ xs: 24, md: 40 }} fontWeight={700}>
						{'발전소 모니터링\n발전왕 비즈에선 쉽습니다.'}
					</Typography>
					<List
						sx={{
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'flex-start',
							gap: '10px',
							fontSize: { xs: 13, md: 16 },
							fontWeight: 700,
							lineHeight: 1.5,
							color: 'grey.600',
							marginTop: 5,
							padding: 0,
							'.MuiListItem-root': {
								width: 'fit-content',
								padding: 0,
								'.MuiListItemButton-root': {
									bgcolor: 'common.white',
									borderWidth: 1,
									borderStyle: 'solid',
									borderColor: 'grey.300',
									borderRadius: 99,
									padding: { xs: '13px 18px', md: '13px 24px' },
									'&.active': {
										color: 'common.white',
										bgcolor: 'common.black',
										borderColor: 'common.black',
									},
								},
							},
						}}
					>
						{tabs.map(({ label, value }, i) => (
							<ListItem key={i}>
								<ListItemButton
									className={tab === value ? 'active' : undefined}
									onClick={() => {
										setTab(value as typeof tab);
									}}
								>
									{label}
								</ListItemButton>
							</ListItem>
						))}
					</List>
					<Grid position="relative" height={94}>
						{tabs.map(({ value, description }, i) => (
							<Fade key={i} in={tab === value}>
								<Box>
									<Typography
										position="absolute"
										fontSize={{ xs: 14, md: 16 }}
										lineHeight={1.5}
										color="grey.A400"
										marginTop="30px"
										marginBottom={{ xs: '14px', md: 2 }}
										whiteSpace={{ xs: 'normal', md: 'pre-line' }}
									>
										{description}
									</Typography>
								</Box>
							</Fade>
						))}
					</Grid>
				</Grid>
				<Grid position="relative" container flex={1} width="100%">
					<Grid
						position="relative"
						container
						justifyContent="center"
						alignItems="center"
						flex={1}
						width="100%"
						marginTop={{ xs: '20px', md: 0 }}
						sx={{
							aspectRatio: { xs: 4 / 3, md: 1 },
							'& img': {
								position: 'absolute',
								width: '100%',
								height: 'auto',
							},
						}}
					>
						{isMobile ? (
							<>
								{tabs.map(({ image: { mobile: image }, value }, i) => (
									<Fade key={i} in={tab === value}>
										<Image src={image} alt="" />
									</Fade>
								))}
							</>
						) : (
							<>
								{tabs.map(({ image: { desktop: image }, value }, i) => (
									<Fade key={i} in={tab === value}>
										<Image src={image} alt="" />
									</Fade>
								))}
							</>
						)}
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
}
